import type {
	FollowUpAgentStatusEnum,
	OrderFilterDto,
	OrderPartnerReviewRatingEnum,
	OrderStatus,
} from "Infrastructure/Api/Api";
import { stringToBoolean } from "Infrastructure/Utilities/Helpers/convertStringToBool";
import { useMarket } from "Market/Providers/CurrentMarketProvider";
import { filteredUserIdAtom } from "Order/Atom/filteredUserIdAtom";
import { selectedVerticalsAtom } from "Shared/Atoms/verticalAtom";
import { DEFAULT_VERTICALS } from "Shared/consts";
import { useAtom } from "jotai";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

export const useOrderFilters = (): OrderFilterDto => {
	const { market } = useMarket();
	const [query] = useSearchParams();
	const [filteredUserId] = useAtom(filteredUserIdAtom);
	const [selectedVerticals, _] = useAtom(selectedVerticalsAtom);

	return useMemo(
		() => ({
			status: (query.getAll("status") as OrderStatus[]) ?? undefined,
			dateNextActionFrom: query.get("dateNextActionFrom") ?? undefined,
			dateNextActionTo: query.get("dateNextActionTo") ?? undefined,
			dateStartingFrom: query.get("dateStartingFrom") ?? undefined,
			dateStartingTo: query.get("dateStartingTo") ?? undefined,
			isNew: query.get("isNew") ? query.get("isNew") === "true" : undefined,
			isHasClaim: stringToBoolean(query.get("isHasClaim")),
			marketId: market?.id,
			partnerId: query.get("partnerId")
				? Number(query.get("partnerId"))
				: undefined,
			unassignedPartner: stringToBoolean(query.get("unassignedPartner")),
			userId: query.get("userId")
				? Number(query.get("userId"))
				: (filteredUserId ?? undefined),
			unassignedUser: stringToBoolean(query.get("unassignedUser")),
			regionId: query.get("regionId")
				? Number(query.get("regionId"))
				: undefined,
			query: query.get("query") ?? undefined,
			review:
				(query.get("review") as OrderPartnerReviewRatingEnum) ?? undefined,
			notReviewed: stringToBoolean(query.get("notReviewed")),
			hasExamination: stringToBoolean(query.get("hasExamination")),
			b2b: query.get("b2b") ?? undefined,
			promoCode: query.get("promoCode") ?? undefined,
			claimDateStartingFrom: query.get("claimDateStartingFrom") ?? undefined,
			claimDateNextActionTo: query.get("claimDateNextActionTo") ?? undefined,
			verticals:
				selectedVerticals.length > 0
					? selectedVerticals.join(",")
					: DEFAULT_VERTICALS,
			followUpAgentStatus:
				(query.getAll("followUpAgentStatus") as FollowUpAgentStatusEnum[])
					.length > 0
					? (query.getAll("followUpAgentStatus") as FollowUpAgentStatusEnum[])
					: undefined,
		}),
		[query, market?.id, filteredUserId, selectedVerticals],
	);
};
